<template>
    <a-spin tip="Carregando..." :spinning="spinning">
        <div class="container">
            <a-card>
                <template #title>
                    <a-breadcrumb separator=">">
                        <a-breadcrumb-item>
                            <FileOutlined />
                            <a href="/maps/layers"><span>Camadas</span></a>
                        </a-breadcrumb-item>
                        <a-breadcrumb-item>
                            {{ isEdit ? 'Editar Camada' : 'Cadastrar Camada' }}
                        </a-breadcrumb-item>
                    </a-breadcrumb>
                </template>

                <a-form
                    ref="formRef"
                    layout="vertical"
                    :model="form"
                    :rules="baseRules"
                    validate-on-rule-change
                >
                    <!-- 1ª linha -->
                    <a-row :gutter="[16,8]">
                        <a-col :md="8">
                            <a-form-item name="name" label="Nome">
                                <a-input v-model:value="form.name" allowClear />
                            </a-form-item>
                        </a-col>
                        <a-col :md="8">
                            <a-form-item name="description" label="Descrição">
                                <a-input v-model:value="form.description" allowClear />
                            </a-form-item>
                        </a-col>

                        <a-col :md="form.layer_type === 'ImageLayer' ? 5 : 8">
                            <a-form-item name="layer_type" label="Tipo de Camada">
                                <a-select
                                    v-model:value="form.layer_type"
                                    :options="layerTypesOptions"
                                    placeholder="Selecione"
                                    @change="onChangeLayerType"
                                />
                            </a-form-item>
                        </a-col>

                        <a-col :md="3" v-if="form.layer_type === 'ImageLayer'">
                            <a-form-item name="is_raster" label="Camada Raster:">
                                <a-switch v-model:checked="form.is_raster" />
                            </a-form-item>
                        </a-col>
                    </a-row>

                    <!-- 2ª linha: LayerGroup (SEM ALTERAÇÕES) -->
                    <a-row v-if="form.layer_type === 'LayerGroup'" :gutter="[16,8]">
                        <a-col :md="8">
                            <a-form-item label="Selecionar Camadas Filhas">
                                <a-select
                                    v-model:value="form.layers"
                                    mode="multiple"
                                    allowClear
                                    placeholder="Selecione camadas"
                                >
                                    <a-select-option
                                        v-for="c in layersWithOutGroup"
                                        :key="c.id"
                                        :value="c.id"
                                    >
                                        {{ c.name }}
                                    </a-select-option>
                                </a-select>
                            </a-form-item>
                        </a-col>

                        <a-col :md="4">
                            <a-form-item label="Visível no LayerSwitcher" class="nowrap-item">
                                <a-switch v-model:checked="form.display_in_layer_switcher" />
                            </a-form-item>
                        </a-col>

                        <a-col :md="4">
                            <a-form-item label="Ativo" class="nowrap-item">
                                <a-switch v-model:checked="form.active" />
                            </a-form-item>
                        </a-col>

                        <a-col :md="4">
                            <a-form-item label="Público" class="nowrap-item">
                                <a-switch v-model:checked="form.is_public" @change="onToggleIsPublic" />
                            </a-form-item>
                        </a-col>

                        <a-col v-if="form.is_public" :md="8">
                            <a-form-item label="Atributos públicos" class="compact-public-attrs">
                                <a-select
                                    v-model:value="form.public_visible_attributes_json"
                                    mode="multiple"
                                    allow-clear
                                    show-search
                                    size="small"
                                    :max-tag-count="2"
                                    placeholder="Campos visíveis"
                                    :options="publicAttributeOptions"
                                    :disabled="!resolvedGeoTableForMetadata"
                                />
                            </a-form-item>
                        </a-col>
                    </a-row>

                    <!-- 2ª linha: NÃO LayerGroup -->
                    <a-row v-else :gutter="[16,8]">
                        <!-- 1) Origem da Camada -->
                        <a-col :md="8">
                            <a-form-item
                                name="layer_source"
                                label="Origem da Camada"
                                :rules="rulesLayerSource"
                            >
                                <a-select
                                    v-model:value="form.layer_source"
                                    :options="layerSourcesOptions"
                                    placeholder="Selecione"
                                />
                            </a-form-item>
                        </a-col>

                        <!-- 2) Tabela Geométrica (Vector) -->
                        <a-col v-if="form.layer_type === 'VectorLayer'" :md="8">
                            <a-form-item
                                name="geo_table"
                                label="Tabela Geométrica"
                                :rules="rulesGeoTable"
                            >
                                <a-select
                                    v-model:value="form.geo_table"
                                    :options="geoTables.map(t => ({ label: t.name, value: t.name }))"
                                    placeholder="Selecione"
                                    allowClear
                                    show-search
                                />
                            </a-form-item>
                        </a-col>

                        <!-- 2) Camada do Geo Server (Image) -->
                        <a-col v-if="form.layer_type === 'ImageLayer' || form.layer_type === 'TileLayer'" :md="8">
                            <a-form-item
                                v-if="!form.external_layer"
                                name="geo_server_layer"
                                label="Camada do Geo Server"
                                :rules="rulesGeoServerLayer"
                            >
                                <a-select
                                    v-model:value="form.geo_server_layer"
                                    :options="geoServerLayers.map(t => ({ label: t.name, value: t.name }))"
                                    placeholder="Selecione"
                                    allowClear
                                    show-search
                                />
                            </a-form-item>

                            <a-form-item v-else label="Camada do Geo Server">
                                <a-input v-model:value="form.layer" allowClear />
                            </a-form-item>
                        </a-col>

                        <!-- 3) Ação ao clicar -->
                        <a-col :md="8">
                            <a-form-item name="action" label="Ação ao clicar na Geometria/Ponto">
                                <a-select
                                    v-model:value="form.action"
                                    :options="layerActionsOptions"
                                    placeholder="Selecione"
                                    allowClear
                                    :style="{width: showPersonalizadoEditor ? '90%' : '100%'}"
                                    style="flex-grow: 1"
                                    @change="onChangeLayerAction"
                                />
                                <a-tooltip
                                    v-if="showPersonalizadoEditor"
                                    title="Personalizar Visualização dos Atributos"
                                >
                                    <a-button type="default" shape="circle" @click="openAttributeDisplayModal">
                                        <template #icon><EditOutlined /></template>
                                    </a-button>
                                </a-tooltip>
                            </a-form-item>
                        </a-col>
                    </a-row>

                    <!-- 3ª linha -->
                    <a-row :gutter="[16,8]">
                        <a-col :md="8">
                            <a-form-item label="Módulos">
                                <a-select
                                    v-model:value="form.modules"
                                    placeholder="Selecione"
                                    mode="multiple"
                                    :max-tag-count="3"
                                    allow-clear
                                    show-search
                                    :options="modulesOptions"
                                    style="width: 100%"
                                />
                            </a-form-item>
                        </a-col>

                        <template v-if="form.layer_type !== 'LayerGroup'">
                            <a-col :md="8">
                                <a-form-item label="Grupo de Camadas">
                                    <a-select
                                        placeholder="Selecione"
                                        v-model:value="form.layers_group_id"
                                        :options="layerGroups.map(gr => ({ label: gr.name, value: gr.id }))"
                                        allow-clear
                                    />
                                </a-form-item>
                            </a-col>

                            <a-col v-if="(form.layer_type === 'ImageLayer' || form.layer_type === 'TileLayer') && form.external_layer" :md="8">
                                <a-form-item
                                    name="url"
                                    label="URL da Origem"
                                    :rules="rulesUrl"
                                >
                                    <a-input v-model:value="form.url" allowClear />
                                </a-form-item>
                            </a-col>
                        </template>
                    </a-row>

                    <a-row :gutter="[16,8]" class="mb-2">
                        <a-col :span="8">
                            <label class="mb-1">Marcadores</label>
                            <a-list bordered>
                                    <a-list-item v-if="form.layer_type === 'VectorLayer'" class="inline-item">
                                        <a-form-item label="Habilitar Style Code" class="nowrap-item">
                                            <a-switch v-model:checked="form.enable_style_code" @change="onToggleStyleCode" />
                                        </a-form-item>
                                    </a-list-item>

                                    <a-list-item v-if="form.layer_type === 'ImageLayer' || form.layer_type === 'TileLayer'" class="inline-item">
                                        <a-form-item label="Camada Externa" class="nowrap-item">
                                            <a-switch v-model:checked="form.external_layer" @change="onToggleExternal" />
                                        </a-form-item>
                                    </a-list-item>

                                    <a-list-item v-if="form.layer_type === 'ImageLayer' || form.layer_type === 'TileLayer'" class="inline-item">
                                        <a-form-item label="Habilitar Parâmetros" class="nowrap-item">
                                            <a-switch v-model:checked="form.enable_params" @change="onToggleParams" />
                                        </a-form-item>
                                    </a-list-item>

                                    <a-list-item class="inline-item">
                                        <a-form-item label="Visível no LayerSwitcher" class="nowrap-item">
                                            <a-switch v-model:checked="form.display_in_layer_switcher" />
                                        </a-form-item>
                                    </a-list-item>

                                    <a-list-item class="inline-item">
                                        <a-form-item label="Ativo" class="nowrap-item">
                                            <a-switch v-model:checked="form.active" />
                                        </a-form-item>
                                    </a-list-item>

                                    <a-list-item class="inline-item">
                                        <a-form-item label="Público" class="nowrap-item">
                                            <a-switch v-model:checked="form.is_public" @change="onToggleIsPublic" />
                                        </a-form-item>
                                    </a-list-item>
                            </a-list>
                        </a-col>

                        <a-col :span="16" v-if="form.layer_type === 'ImageLayer' || form.layer_type === 'TileLayer' && form.enable_params">
                            <a-form-item
                                name="params"
                                label="Parâmetros (JSON)"
                                :rules="rulesParams"
                            >
                                <Codemirror
                                    v-model:value="form.params"
                                    :options="cmJsonOptions"
                                    :height="'100%'"
                                    class="cm-box"
                                />
                            </a-form-item>
                        </a-col>

                        <a-col :span="16" v-if="form.layer_type === 'VectorLayer' && form.enable_style_code">
                            <a-form-item
                                name="style_code"
                                label="Style Code (JavaScript)"
                                :rules="rulesStyleCode"
                            >
                                <Codemirror
                                    v-model:value="form.style_code"
                                    :options="cmJsOptions"
                                    class="cm-box"
                                />
                            </a-form-item>
                        </a-col>
                    </a-row>

                    <a-row :gutter="[16,8]">
                        <a-col v-if="form.is_public" :md="8" class="nowrap-col">
                            <a-form-item label="Atributos públicos" class="compact-public-attrs">
                                <a-select
                                    v-model:value="form.public_visible_attributes_json"
                                    mode="multiple"
                                    allow-clear
                                    show-search
                                    :max-tag-count="2"
                                    placeholder="Campos visíveis"
                                    :options="publicAttributeOptions"
                                    :disabled="!resolvedGeoTableForMetadata"
                                    style="width: 100%"
                                />
                            </a-form-item>
                        </a-col>
                    </a-row>

                    <!-- Botões -->
                    <a-form-item>
                        <a-tooltip :title="isEdit ? 'Salvar Alterações' : 'Salvar'">
                            <a-button size="large" type="primary" @click="save" class="center-flex">
                                <template #icon><SaveOutlined style="font-size: 18px;" /></template>
                            </a-button>
                        </a-tooltip>
                    </a-form-item>
                </a-form>
            </a-card>
        </div>

        <a-modal
            v-model:open="attributeDisplayModalOpen"
            title="Personalizar Visualização dos Atributos"
            width="920px"
            :footer="null"
            destroy-on-close
            @cancel="attributeDisplayModalOpen = false"
        >
            <a-spin :spinning="geoTableMetaLoading">
                <div v-if="resolvedGeoTableForMetadata" class="attribute-display-meta">
                    <p class="vars-intro">
                        Use os nomes dos campos como variáveis no HTML no formato <code>$nome_do_campo</code>.
                        Comentários por coluna:
                    </p>
                    <ul class="column-var-list">
                        <li v-for="col in geoTableColumnsFiltered" :key="col.column_name">
                            <code>{{ '$' + col.column_name }}</code>
                            <span v-if="col.description" class="col-desc"> — {{ col.description }}</span>
                            <span v-else class="muted"> — sem comentário na coluna</span>
                        </li>
                    </ul>
                </div>
                <p v-else class="muted">
                    Para camada vetorial, selecione a tabela geométrica. Para camada de imagem ou tile (WMS/WMTS), selecione a camada do Geo Server
                    — o nome da tabela no PostgreSQL é a parte após o caractere «:» no nome da camada (ex.: <code>esquema:minha_tabela</code>).
                    Na camada externa, use o mesmo formato no campo da camada.
                </p>

                <div class="attribute-display-editor">
                    <Codemirror
                        v-model:value="form.attribute_display_html"
                        :options="cmHtmlOptions"
                        :height="'420px'"
                        class="cm-component"
                        :border="true"
                    />
                </div>
            </a-spin>
            <div class="attribute-display-modal-footer">
                <a-button type="primary" @click="attributeDisplayModalOpen = false">Fechar</a-button>
            </div>
        </a-modal>
    </a-spin>
</template>

<script setup>
import { reactive, ref, computed, onMounted, watch } from 'vue'
import { FileOutlined, SaveOutlined, EditOutlined } from '@ant-design/icons-vue'
import Codemirror from 'codemirror-editor-vue3'
import 'codemirror/theme/neat.css'
import 'codemirror/mode/javascript/javascript.js'
import 'codemirror/mode/htmlmixed/htmlmixed'
import { notification } from 'ant-design-vue'
import { openNotification } from '../../../../utils/utilities'

const props = defineProps({ id: { type: [Number, String], default: null } })
const currentId = computed(() => props.id)
const isEdit = computed(() => !!props.id)
const route = '/api/maps/layers'
const spinning = ref(false)
const formRef = ref(null)

/** Campos enviados ao API — sem relações/metadados do GET (permission, author_user, etc.). */
const LAYER_PAYLOAD_KEYS = [
    'name',
    'description',
    'layer_type',
    'layer_source',
    'geo_table',
    'geo_server_layer',
    'layer',
    'url',
    'params',
    'style_code',
    'display_in_layer_switcher',
    'active',
    'is_public',
    'external_layer',
    'enable_style_code',
    'is_raster',
    'faq_id',
    'layers_group_id',
    'action',
    'modules',
    'layers',
    'attribute_display_html',
    'public_visible_attributes_json',
]

const form = reactive({
    name: '',
    description: '',
    layer_type: null,
    layer_source: null,
    geo_table: '',
    geo_server_layer: null,
    layer: '',
    url: '',
    params: null,
    style_code: null,
    display_in_layer_switcher: true,
    active: true,
    is_public: false,
    external_layer: false,
    enable_params: false,
    enable_style_code: false,
    is_raster: false,
    faq_id: null,
    layers_group_id: null,
    action: null,
    modules: [],
    layers: [],
    attribute_display_html: '',
    public_visible_attributes_json: []
})

const applyLoadedLayerToForm = (loaded) => {
    for (const key of LAYER_PAYLOAD_KEYS) {
        if (key === 'modules' || key === 'layers' || key === 'attribute_display_html') {
            continue
        }
        if (Object.prototype.hasOwnProperty.call(loaded, key) && loaded[key] !== undefined) {
            form[key] = loaded[key]
        }
    }

    form.modules = Array.isArray(loaded.modules) ? loaded.modules.map((module) => module.id) : []
    form.attribute_display_html = loaded.attribute_display_html ?? ''
    form.public_visible_attributes_json = Array.isArray(loaded.public_visible_attributes_json)
        ? loaded.public_visible_attributes_json
        : []
    form.enable_params = Boolean(form.params && String(form.params).trim().length > 0)

    if (Array.isArray(loaded.layers)) {
        loaded.layers.forEach((layer) => layersWithOutGroup.value.push(layer))
        form.layers = loaded.layers.map((layer) => layer.id)
    } else {
        form.layers = []
    }
}

const buildLayerPayload = () => {
    const payload = {}
    for (const key of LAYER_PAYLOAD_KEYS) {
        payload[key] = form[key]
    }
    payload.layers = form.layers ?? null
    payload.layers_group_id = form.layers_group_id ?? null
    payload.action = form.action ?? null
    return payload
}

const layerGroups = ref([])
const allLayers = ref([])
const layersWithOutGroup = ref([])
const geoTables = ref([])
const geoServerLayers = ref([])
const layerTypesOptions = ref([])
const layerSourcesOptions = ref([])
const layerActionsOptions = ref([])
const modulesOptions = ref([])

const cmJsonOptions = { mode: 'application/json', theme: 'neat', lineNumbers: true }
const cmJsOptions = { mode: 'javascript', theme: 'neat', lineNumbers: true }
const cmHtmlOptions = {
    mode: 'text/html',
    theme: 'default',
    readOnly: false,
    lineNumbers: true,
    lineWiseCopyCut: true,
    gutters: ['CodeMirror-lint-markers'],
    lint: true
}

const attributeDisplayModalOpen = ref(false)
const geoTableMetaLoading = ref(false)
const geoTableMeta = ref(null)

const showPersonalizadoEditor = computed(() =>
    ['VectorLayer', 'ImageLayer', 'TileLayer'].includes(form.layer_type) && form.action === 'Personalizado'
)

/** Parte após o primeiro «:» no nome da camada do Geo Server (mesma regra usada no visualizador de camadas). */
function tableNameFromGeoServerLayer (value) {
    if (value == null || value === '') return null
    const s = String(value).trim()
    if (!s) return null
    const idx = s.indexOf(':')
    if (idx === -1) return s
    return s.slice(idx + 1).trim() || null
}

/** Nome qualificado schema.tabela usado no dicionário / comentários PostgreSQL. */
const resolvedGeoTableForMetadata = computed(() => {
    if (form.layer_type === 'VectorLayer') {
        const t = form.geo_table && String(form.geo_table).trim()
        return t || null
    }
    if (form.layer_type === 'ImageLayer' || form.layer_type === 'TileLayer') {
        if (form.external_layer) {
            return tableNameFromGeoServerLayer(form.layer)
        }
        return tableNameFromGeoServerLayer(form.geo_server_layer)
    }
    return null
})

const geoTableColumnsFiltered = computed(() => {
    const cols = geoTableMeta.value?.columns
    if (!Array.isArray(cols)) return []
    return cols.filter(c => c.column_name !== 'geom')
})

const publicAttributeOptions = computed(() =>
    geoTableColumnsFiltered.value.map((col) => ({
        label: `${col.column_name}${col.description ? ` - ${col.description}` : ''}`,
        value: col.column_name
    }))
)

const collectLayerNames = (layers) => {
    const names = []
    for (const layer of layers) {
        if (Number(layer.id) !== Number(props.id)) {
            names.push(String(layer.name).trim().toLowerCase())
        }
        if (Array.isArray(layer.layers) && layer.layers.length) {
            names.push(...collectLayerNames(layer.layers))
        }
    }
    return names
}

const validateUniqueName = async (_rule, value) => {
    if (!value) return Promise.resolve()

    const normalized = String(value).trim().toLowerCase()

    if (normalized === 'padrão') return Promise.reject('Não é possível criar uma camada padrão.')
    if (normalized === 'multifinalitário') return Promise.reject('Não é possível criar uma camada com esse nome.')

    if (collectLayerNames(allLayers.value).includes(normalized)) {
        return Promise.reject('Já existe uma camada com esse nome.')
    }

    return Promise.resolve()
}

/* Regras sempre obrigatórias */
const baseRules = reactive({
    name: [
        { required: true, message: 'Informe o nome' },
        { validator: validateUniqueName, trigger: 'blur' }
    ],
    description: [{ required: true, message: 'Informe a descrição' }],
})

/* Regras dinâmicas por item (sem Promise.reject; usamos throw) */
const rulesLayerSource = [
    {
        async validator () {
            if (form.layer_type === 'LayerGroup') return
            if (!form.layer_source) throw new Error('Selecione a origem da camada')
        },
        trigger: ['change', 'blur']
    }
]

const rulesGeoTable = [
    {
        async validator () {
            if (form.layer_type !== 'VectorLayer') return
            if (!form.geo_table) throw new Error('Selecione a tabela geométrica')
        },
        trigger: ['change', 'blur']
    }
]

const rulesGeoServerLayer = [
    {
        async validator () {
            if (form.layer_type !== 'ImageLayer' || form.external_layer) return
            if (!form.geo_server_layer) throw new Error('Selecione a camada do Geo Server')
        },
        trigger: ['change', 'blur']
    }
]

const rulesUrl = [
    {
        async validator () {
            if (!(form.layer_type === 'ImageLayer' && form.external_layer)) return
            if (!form.url) throw new Error('Informe a URL da origem')
            try { new URL(form.url) } catch { throw new Error('Informe uma URL válida (ex.: https://servidor/camada)') }
        },
        trigger: ['change', 'blur']
    }
]

const rulesParams = [
    {
        async validator () {
            if (!(form.layer_type === 'ImageLayer' && form.enable_params)) return
            if (!form.params || String(form.params).trim().length === 0) {
                throw new Error('Informe os parâmetros em JSON')
            }
            try { JSON.parse(form.params) } catch { throw new Error('JSON inválido nos parâmetros') }
        },
        trigger: ['change', 'blur']
    }
]

const rulesStyleCode = [
    {
        async validator () {
            if (!(form.layer_type === 'VectorLayer' && form.enable_style_code)) return
            if (!form.style_code || String(form.style_code).trim().length === 0) {
                throw new Error('Informe o Style Code')
            }
        },
        trigger: ['change', 'blur']
    }
]

/* Loaders */
const getLayerTypes = async () => {
    layerTypesOptions.value = (await window.axios.get(`${route}/layer-types`)).data.data
    // garante formato correto, caso API retorne strings:
    layerTypesOptions.value = layerTypesOptions.value.map(opt =>
        typeof opt === 'string' ? { label: opt, value: opt } : opt
    )
    form.layer_type = 'VectorLayer'
}

const getLayerGroups = async () => {
    layerGroups.value = (await window.axios.get(`${route}?onlyGroups`)).data.data
}

const getAllLayers = async () => {
    allLayers.value = (await window.axios.get(route)).data.data
}

const getLayersWithOutGroup = async () => {
    layersWithOutGroup.value = (await window.axios.get(`${route}?onlyIsNotGroup`)).data.data
}

const getGeoTables = async () => {
    geoTables.value = (await window.axios.get(`${route}/geo-tables`)).data.data
}

const getGeoServerLayers = async () => {
    geoServerLayers.value = (await window.axios.get(`${route}/geo-server-layers`)).data.data
}

const getLayerActions = async () => {
    let data = (await window.axios.get(`${route}/layer-actions`)).data.data
    // normaliza para [{label,value}]
    layerActionsOptions.value = (Array.isArray(data) ? data : []).map(v =>
        typeof v === 'string' ? { label: v, value: v } : v
    )
}

const getModules = async () => {
    modulesOptions.value = (await window.axios.get(`${route}/modules`)).data.data.map(mod => {
        return { label: mod.name, value: mod.id }
    })
}

const getLayer = async (id) => {
    return (await window.axios.get(`${route}/${id}`)).data.data
}

const loadData = async () => {
    await getGeoServerLayers()
    await getGeoTables()
    await getLayersWithOutGroup()
    await getLayerGroups()
    await getAllLayers()
    await getLayerTypes()
    await getLayerActions()
    await getModules()

    // prepara allowedSources conforme tipo default
    const typeDef = layerTypesOptions.value.find(t => t.value === form.layer_type)
    const rawSources = typeDef ? typeDef.allowedSources ?? [] : []
    layerSourcesOptions.value = rawSources.map(s => (typeof s === 'string' ? { label: s, value: s } : s))

    if (isEdit.value) {
        const loaded = await getLayer(currentId.value)
        applyLoadedLayerToForm(loaded)

        // refaz allowedSources com base no tipo carregado do backend
        const def = layerTypesOptions.value.find(t => t.value === form.layer_type)
        const srcs = def ? def.allowedSources ?? [] : []
        layerSourcesOptions.value = srcs.map(s => (typeof s === 'string' ? { label: s, value: s } : s))
    }
}

const syncPublicAttributesAgainstMetadata = () => {
    const allowed = new Set(publicAttributeOptions.value.map(o => o.value))
    form.public_visible_attributes_json = (Array.isArray(form.public_visible_attributes_json) ? form.public_visible_attributes_json : [])
        .filter((name) => allowed.has(name))
}

/* helpers limpeza validação */
const clearValidate = async (fields = []) => {
    try { await formRef.value?.clearValidate(fields) } catch {}
}

/* mudanças */
const onChangeLayerType = async () => {
    const newVal = form.layer_type

    const resetVector = () => {
        form.enable_style_code = false
        form.style_code = null
        form.geo_table = ''
        form.attribute_display_html = ''
    }
    const resetImageAndTile = () => {
        form.external_layer = false
        form.enable_params = false
        form.params = null
        form.url = ''
        form.geo_server_layer = null
        form.layer = ''
    }

    if (newVal === 'VectorLayer') resetVector()
    else if (newVal === 'ImageLayer' || newVal === 'TileLayer') resetImageAndTile()
    else { resetVector(); resetImage() } // LayerGroup

    const typeDef = layerTypesOptions.value.find(t => t.value === newVal)
    const rawSources = typeDef ? typeDef.allowedSources ?? [] : []
    layerSourcesOptions.value = rawSources.map(s => (typeof s === 'string' ? { label: s, value: s } : s))

    await clearValidate(['layer_source', 'geo_table', 'geo_server_layer', 'url', 'params', 'style_code'])
}

const onToggleExternal = async () => {
    if (form.external_layer) {
        form.geo_server_layer = null
    } else {
        form.url = ''
        form.layer = ''
    }
    await clearValidate(['geo_server_layer', 'url'])
}

const onToggleParams = async () => {
    if (!form.enable_params) form.params = null
    await clearValidate(['params'])
}

const onToggleStyleCode = async () => {
    if (!form.enable_style_code) form.style_code = null
    await clearValidate(['style_code'])
}

const onToggleIsPublic = async () => {
    if (!form.is_public) {
        form.public_visible_attributes_json = []
        return
    }
    if (resolvedGeoTableForMetadata.value) {
        await loadGeoTableMeta()
        syncPublicAttributesAgainstMetadata()
    }
}

async function save () {
    try {
        await formRef.value?.validate()

        const payload = buildLayerPayload()

        if (isEdit.value) {
            const response = await window.axios.put(`${route}/${currentId.value}`, payload)
            openNotification('success', response.data.message || 'Camada atualizada!')
            await loadData()
        } else {
            const response = await window.axios.post(route, payload)
            openNotification('success', response.data.message || 'Camada criada!')
            const newId = response?.data?.data?.id ?? currentId.value
            if (newId) window.location.href = `/maps/layers/${newId}/edit`
        }
    } catch (e) {
        const backendNameError = e?.response?.data?.errors?.name?.[0]
        if (backendNameError) {
            openNotification('error', backendNameError)
            return
        }
        openNotification('warning', 'Preencha os campos obrigatórios destacados.')
        if (e?.message && !e?.errorFields) openNotification('error', e.message)
    }
}

const onChangeLayerAction = () => {
    if (form.action !== 'Personalizado') {
        form.attribute_display_html = ''
    }
}

const loadGeoTableMeta = async () => {
    const table = resolvedGeoTableForMetadata.value
    if (!table) {
        geoTableMeta.value = null
        return
    }
    geoTableMetaLoading.value = true
    try {
        const { data } = await window.axios.get(`${route}/geo-table-metadata`, {
            params: { table }
        })
        geoTableMeta.value = data.data
    } catch (e) {
        geoTableMeta.value = null
        openNotification('error', e?.response?.data?.message || 'Não foi possível carregar os metadados da tabela.')
    } finally {
        geoTableMetaLoading.value = false
    }
}

const openAttributeDisplayModal = async () => {
    if (!resolvedGeoTableForMetadata.value) {
        if (form.layer_type === 'ImageLayer' || form.layer_type === 'TileLayer') {
            openNotification(
                'warning',
                'Selecione a camada do Geo Server (ou informe a camada na camada externa). O nome da tabela no banco é a parte após o caractere «:» no nome da camada.'
            )
        } else {
            openNotification('warning', 'Selecione a tabela geométrica para visualizar os campos e editar o HTML.')
        }
        return
    }
    attributeDisplayModalOpen.value = true
    await loadGeoTableMeta()
}

onMounted(loadData)

watch(
    () => resolvedGeoTableForMetadata.value,
    async (table) => {
        if (!form.is_public || !table) return
        await loadGeoTableMeta()
        syncPublicAttributesAgainstMetadata()
    }
)
</script>

<style scoped lang="scss">
.cm-box {
    border: 1px solid #ddd;
    border-radius: 4px;
}

.cm-component {
    font-family: monospace;
}

.attribute-display-meta {
    margin-bottom: 12px;
}

.attribute-display-editor {
    margin-top: 8px;
}

.attribute-display-modal-footer {
    margin-top: 16px;
    text-align: right;
}

.compact-public-attrs :deep(.ant-select) {
    width: 100%;
}

.muted {
    color: #888;
}

.vars-intro {
    margin: 8px 0;
}

.column-var-list {
    margin: 0 0 12px 1.2em;
    padding: 0;
}

.column-var-list .col-desc {
    color: #555;
}

.nowrap-col {
    display: flex;
    align-items: end;
}

.nowrap-col :deep(.ant-form-item) {
    width: 100%;
}

.nowrap-item :deep(.ant-form-item-label > label) {
    white-space: nowrap;
}
.nowrap-item :deep(.ant-form-item-control) {
    white-space: nowrap;
}

.inline-item {
    padding-top: 15px;
    padding-bottom: 15px;
}

.inline-item :deep(.ant-form-item) {
    margin-bottom: 0 !important;
    width: 100%;
}

.inline-item :deep(.ant-form-item-row) {
    display: flex !important;
    flex-direction: row !important;
    align-items: center !important;
    justify-content: space-evenly !important;
}

.inline-item :deep(.ant-form-item-label) {
    padding: 0;
    display: flex;
    align-items: center;
    flex: 1 1 auto;
}

.inline-item :deep(.ant-form-item-label > label) {
    margin: 0;
}

.inline-item :deep(.ant-form-item-control) {
    flex: 0 0 auto;
    width: min-content;
}

.inline-item :deep(.ant-form-item-control-input) {
    min-height: auto;
}

.inline-item :deep(.ant-form-item-control-input-content) {
    display: flex;
    justify-content: flex-end;
}
</style>
